'use client';

import { useCallback } from 'react';
import type { UpdateApplicationRequestDto } from '@terra/shared-contracts-applications';
import { toasterApi } from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import { useUpdateApplicationFields } from './useUpdateApplicationFields';

export function usePersistApplicationSettings({
  applicationId,
}: {
  applicationId: string | undefined;
}) {
  const t = useTranslations('settings');
  const { mutateAsync, isPending } = useUpdateApplicationFields();

  const persistApplicationSettings = useCallback(
    async ({
      updates,
      showSuccessToast = true,
    }: {
      updates: Partial<UpdateApplicationRequestDto>;
      showSuccessToast?: boolean;
    }): Promise<boolean> => {
      if (!applicationId) {
        return false;
      }

      try {
        await mutateAsync({ applicationId, body: updates });

        if (showSuccessToast) {
          toasterApi.success({ title: t('settingsSaved') });
        }

        return true;
      } catch (error: unknown) {
        toasterApi.error({
          title: t('saveFailed'),
          description:
            error instanceof Error ? error.message : t('saveFailedDescription'),
        });

        return false;
      }
    },
    [applicationId, mutateAsync, t],
  );

  return { isUpdating: isPending, persistApplicationSettings };
}
